// withMixpanelReplay.tsx
import React, {
  ComponentType,
  forwardRef,
  useEffect,
} from "react";
import mixpanel from "mixpanel-browser";

type MixpanelReplayOptions = {
  enabled?: boolean;
  componentName?: string;
  scopeKey?: string;
  register?: Record<string, unknown>;
  startEventName?: string;
  stopEventName?: string;
};

type WithMixpanelReplayExtraProps = {
  // Optional per-instance overrides
  mixpanelReplayOptions?: MixpanelReplayOptions;
};

type OptionsOrResolver<P> =
  | MixpanelReplayOptions
  | ((props: P) => MixpanelReplayOptions);

// Simple ref-count by scope so multiple mounts don’t stop each other
const refCounts: Record<string, number> = {};

function startReplayIfNeeded(scopeKey: string) {
  refCounts[scopeKey] = (refCounts[scopeKey] ?? 0) + 1;
  console.log("startReplayIfNeeded", scopeKey, refCounts[scopeKey])
  if (refCounts[scopeKey] === 1) {
    try {
      mixpanel.start_session_recording()
    } catch (e) {
      console.error("Error starting mixpanel replay", e)
    }
  }
}

function stopReplayIfNeeded(scopeKey: string) {
  refCounts[scopeKey] = Math.max((refCounts[scopeKey] ?? 1) - 1, 0);
  if (refCounts[scopeKey] === 0) {
    try {
      mixpanel.stop_session_recording()
    } catch(e) {
      console.error("Error stopping mixpanel replay", e)
    }
  }
}

function isMixpanelAvailable(): boolean {
  try {
    // Ensure we're in a browser and the mixpanel API surface exists
    return (
      typeof window !== "undefined" &&
      typeof document !== "undefined" &&
       typeof mixpanel !== "undefined"
    );                        
  } catch {
    return false;
  }
}


export function withMixpanelReplay<P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: OptionsOrResolver<P>
) {
  const wrappedName =
    (WrappedComponent as any).displayName ||
    WrappedComponent.name ||
    "Component";

  const WithMixpanelReplay = forwardRef<any, P & WithMixpanelReplayExtraProps>(
    (props, ref) => {
      const { mixpanelReplayOptions, ...rest } = props as P &
        WithMixpanelReplayExtraProps;

      // Resolve options (defaults -> HOC options -> per-instance overrides)
      const resolvedFromParam =
        typeof options === "function" ? (options as (p: P) => MixpanelReplayOptions)(props as P) : options;

      const defaults: MixpanelReplayOptions = {
        enabled: true,
        componentName: wrappedName,
        scopeKey: wrappedName,
        startEventName: `${wrappedName} Opened`,
        stopEventName: `${wrappedName} Closed`,
      };

      const finalOpts: Required<MixpanelReplayOptions> = {
        ...(defaults as Required<MixpanelReplayOptions>),
        ...(resolvedFromParam || {}),
        ...(mixpanelReplayOptions || {}),
      };

      useEffect(() => {
        if (!isMixpanelAvailable()) {
          // Mixpanel not initialized or not available; safely do nothing
          console.log("Mixpanel not available; skipping replay setup")
          return;
        }

        if (typeof window === "undefined") return;
        if (!finalOpts.enabled) return;

        try {
          mixpanel.register({
            feature: finalOpts.componentName,
            ...(finalOpts.register || {}),
          });
          if (finalOpts.startEventName) {
            mixpanel.track(finalOpts.startEventName);
          }
        } catch {
          // no-op
        }

        startReplayIfNeeded(finalOpts.scopeKey);

        return () => {
          try {
            if (finalOpts.stopEventName) {
              mixpanel.track(finalOpts.stopEventName);
            }
          } catch {
            // no-op
          }
          stopReplayIfNeeded(finalOpts.scopeKey);
        };
        // Intentional: we don’t re-run when props change to keep a stable session
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []); // mount/unmount only

      return <WrappedComponent ref={ref} {...(rest as P)} />;
    }
  );

  (WithMixpanelReplay as any).displayName = `withMixpanelReplay(${wrappedName})`;

  return WithMixpanelReplay;
}
